import {
  err,
  normalizeContentRegion,
  ok,
  parseFeedItems,
  parseHashtagFeed,
  parseSearchSuggestions,
  resolveInstanceUrl,
  searchWireParams,
  withQuery,
  type AbortLike,
  type FeedItem,
  type ParsedList,
  type Result,
  type SearchQuery,
  type SearchSuggestions,
  type VideoSummary,
} from '@visious/core';
import type { Platform } from '../platform/instance';
import { fetchJson } from './json';
import type { ApiProblem } from './videos';

/**
 * §8's Search row, plus the hashtag page that shares its shape. Three endpoints, three envelopes —
 * which is the thing to keep straight: `/search` is a bare array, `/search/suggestions` is
 * `{query,suggestions[]}` and `/hashtag/{tag}` is `{results[]}`.
 *
 * The bodies are read through `api/json.ts` since 4a, so a zero-byte `200` is its own state here too
 * rather than the sentence about a proxy.
 */
export type SearchResult = Result<ParsedList<FeedItem>, ApiProblem>;
export type HashtagResult = Result<ParsedList<VideoSummary>, ApiProblem>;
export type SuggestionsResult = Result<SearchSuggestions, ApiProblem>;

/**
 * `region` is a device preference rather than part of the query (`search/query.ts`), so it arrives
 * separately and goes through the same validation Trending uses. Search is the more forgiving of the
 * two — an unsupported code answers `200` here where Trending answers a leaked `KeyError` 500 — but
 * one rule for both beats remembering which endpoint tolerates what.
 */
export async function fetchSearch(
  platform: Platform,
  query: SearchQuery,
  region: string | null,
  signal?: AbortLike,
): Promise<SearchResult> {
  const path = '/api/v1/search';
  const base = resolveInstanceUrl(platform.config, path);
  if (base === null) return err({ kind: 'bad-url', path });

  const url = withQuery(base, {
    ...searchWireParams(query, { region: normalizeContentRegion(region) }),
  });
  if (url === null) return err({ kind: 'bad-url', path });

  const body = await fetchJson(platform, url, signal);
  if (!body.ok) return err(body.error);

  const parsed = parseFeedItems(body.value, platform.parseContext);
  return envelopeFailure(parsed) ?? ok(parsed);
}

export async function fetchHashtag(
  platform: Platform,
  tag: string,
  page: number,
  signal?: AbortLike,
): Promise<HashtagResult> {
  // The tag is a path segment, and it must arrive **without** its `#`: `/api/v1/hashtag/%23lofi`
  // answers `200 {"results":[]}`, which is indistinguishable from a tag nobody has used.
  // `parseRoute` already strips it; this is the second line of defence for a hand-built call.
  const clean = tag.startsWith('#') ? tag.slice(1) : tag;
  const path = `/api/v1/hashtag/${encodeURIComponent(clean)}`;
  const base = resolveInstanceUrl(platform.config, path);
  if (base === null) return err({ kind: 'bad-url', path });

  const url = withQuery(base, { page: page > 1 ? String(page) : null });
  if (url === null) return err({ kind: 'bad-url', path });

  const body = await fetchJson(platform, url, signal);
  if (!body.ok) return err(body.error);

  const parsed = parseHashtagFeed(body.value, platform.parseContext);
  return envelopeFailure(parsed) ?? ok(parsed);
}

/**
 * The suggestions list. It is the one request in the app that fires while someone is typing, so it is
 * also the one whose failure must be **completely silent**: a dropdown that does not appear is a
 * dropdown nobody misses, whereas an error panel under a search box while typing is unusable. The
 * caller therefore discards the `err` arm rather than describing it (`useSuggestions`).
 */
export async function fetchSuggestions(
  platform: Platform,
  q: string,
  signal?: AbortLike,
): Promise<SuggestionsResult> {
  const path = '/api/v1/search/suggestions';
  const base = resolveInstanceUrl(platform.config, path);
  if (base === null) return err({ kind: 'bad-url', path });

  const url = withQuery(base, { q });
  if (url === null) return err({ kind: 'bad-url', path });

  const body = await fetchJson(platform, url, signal);
  if (!body.ok) return err(body.error);

  const parsed = parseSearchSuggestions(body.value);
  return parsed.ok ? ok(parsed.value) : err({ kind: 'parse', error: parsed.error });
}

/**
 * The same reading `api/feeds.ts` makes, and for the same reason: a list whose *envelope* was wrong —
 * an object where an array belongs — drops one error and no items, and that is a parse failure rather
 * than an empty result set. A list that dropped some of its rows is not, and R4 says those are
 * counted and shown.
 */
function envelopeFailure<T>(parsed: ParsedList<T>): { ok: false; error: ApiProblem } | null {
  if (parsed.items.length !== 0 || parsed.dropped.length !== 1) return null;
  const error = parsed.dropped[0];
  if (error === undefined || error.reason !== 'wrong-type') return null;
  // `field === null` is a bad top level; `field === 'results'` is the hashtag envelope missing its
  // one key, which is the same class of failure one level in.
  if (error.field !== null && error.field !== 'results') return null;
  return { ok: false, error: { kind: 'parse', error } };
}
